import { Head, Link } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

type Status = 'queued' | 'learning' | 'known'

interface TuneSummary {
  id: number
  name: string
  status: Status
  tune_type: string | null
  musical_key: string | null
  composer: string | null
  last_played_at: string | null
  rusty: boolean
  recordings_count: number
}

interface Props {
  tunes: TuneSummary[]
}

type StatusFilter = Status | 'all'
type Sort = 'name-asc' | 'name-desc' | 'recent' | 'status'

const STATUS: Record<Status, { label: string; dot: string; pill: string; spine: string }> = {
  queued: {
    label: 'Queued',
    dot: 'bg-muted-foreground/45',
    pill: 'border-border text-muted-foreground',
    spine: 'bg-muted-foreground/40',
  },
  learning: {
    label: 'Learning',
    dot: 'bg-learning',
    pill: 'border-learning/30 bg-learning/[0.07] text-learning',
    spine: 'bg-learning',
  },
  known: {
    label: 'Known',
    dot: 'bg-play',
    pill: 'border-play/25 bg-play/[0.05] text-play',
    spine: 'bg-play',
  },
}

const STATUS_FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Queued', value: 'queued' },
  { label: 'Learning', value: 'learning' },
  { label: 'Known', value: 'known' },
]

const SORTS: { label: string; value: Sort }[] = [
  { label: 'A → Z', value: 'name-asc' },
  { label: 'Z → A', value: 'name-desc' },
  { label: 'Recently played', value: 'recent' },
  { label: 'By status', value: 'status' },
]

const STATUS_ORDER: Record<Status, number> = { learning: 0, queued: 1, known: 2 }

const MODE = /(major|minor|dorian|mixolydian|aeolian|ionian|lydian|phrygian|locrian)$/i

function formatKey(key: string) {
  return key.replace(MODE, (m) => ` ${m[0].toUpperCase()}${m.slice(1).toLowerCase()}`).trim()
}

function metaLine(tune: TuneSummary) {
  return [tune.tune_type, tune.musical_key && formatKey(tune.musical_key), tune.composer]
    .filter(Boolean)
    .join('  ·  ')
}

function lastPlayed(iso: string | null) {
  if (!iso) return 'never'
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 31) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function compare(a: TuneSummary, b: TuneSummary, sort: Sort) {
  switch (sort) {
    case 'name-desc':
      return b.name.localeCompare(a.name)
    case 'recent': {
      const at = a.last_played_at ? Date.parse(a.last_played_at) : -Infinity
      const bt = b.last_played_at ? Date.parse(b.last_played_at) : -Infinity
      return bt - at || a.name.localeCompare(b.name)
    }
    case 'status':
      return STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name)
    default:
      return a.name.localeCompare(b.name)
  }
}

function Row({ tune, delay }: { tune: TuneSummary; delay: number }) {
  const s = STATUS[tune.status]
  const meta = metaLine(tune)

  return (
    <Link
      href={`/tunes/${tune.id}`}
      style={{ animationDelay: `${delay}ms` }}
      className="animate-rise group relative flex items-center gap-4 py-3 pl-5 pr-3 transition-colors hover:bg-accent/60"
    >
      <span
        className={cn(
          'absolute inset-y-1.5 left-0 w-0.5 origin-center scale-y-0 rounded-full transition-transform duration-200 group-hover:scale-y-100',
          s.spine,
        )}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <h3 className="truncate font-display text-xl font-medium leading-tight tracking-tight text-foreground decoration-border underline-offset-[5px] group-hover:underline">
            {tune.name}
          </h3>
          {tune.rusty && (
            <span
              className="inline-flex shrink-0 items-center gap-1 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-refresh"
              title="Known but not played in a while"
            >
              <span className="size-1.5 rounded-full bg-refresh" />
              rusty
            </span>
          )}
        </div>
        {meta && (
          <p className="mt-0.5 truncate text-[0.7rem] uppercase tracking-[0.16em] text-muted-foreground">
            {meta}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3 sm:gap-4">
        {tune.recordings_count > 0 && (
          <span
            className="hidden items-center gap-1 text-xs text-muted-foreground/80 sm:inline-flex"
            title={`${tune.recordings_count} recording${tune.recordings_count === 1 ? '' : 's'}`}
          >
            <span aria-hidden>♪</span>
            {tune.recordings_count}
          </span>
        )}
        <span className="hidden w-16 text-right text-xs tabular-nums text-muted-foreground/70 md:inline-block">
          {lastPlayed(tune.last_played_at)}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.68rem] font-medium',
            s.pill,
          )}
        >
          <span className={cn('size-1.5 rounded-full', s.dot)} />
          {s.label}
        </span>
      </div>
    </Link>
  )
}

export default function Index({ tunes }: Props) {
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [rustyOnly, setRustyOnly] = useState(false)
  const [sort, setSort] = useState<Sort>('name-asc')
  const searchRef = useRef<HTMLInputElement>(null)

  const rustyCount = useMemo(() => tunes.filter((t) => t.rusty).length, [tunes])

  const types = useMemo(
    () => [...new Set(tunes.map((t) => t.tune_type).filter((t): t is string => !!t))].sort(),
    [tunes],
  )
  const [type, setType] = useState<string>('all')

  // "/" focuses search, like a proper catalogue.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement
      if (e.key === '/' && !typing) {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return tunes
      .filter((t) => {
        if (status !== 'all' && t.status !== status) return false
        if (rustyOnly && !t.rusty) return false
        if (type !== 'all' && t.tune_type !== type) return false
        if (!q) return true
        return [t.name, t.tune_type, t.musical_key, t.composer]
          .filter(Boolean)
          .some((field) => (field as string).toLowerCase().includes(q))
      })
      .sort((a, b) => compare(a, b, sort))
  }, [tunes, query, status, rustyOnly, type, sort])

  // Letter dividers — the index of a real tunebook — only when sorted by name.
  const grouped = useMemo(() => {
    if (sort !== 'name-asc' && sort !== 'name-desc') return null
    const groups: { letter: string; tunes: TuneSummary[] }[] = []
    for (const tune of visible) {
      const letter = (tune.name[0] || '#').toUpperCase()
      const head = /[A-Z]/.test(letter) ? letter : '#'
      const last = groups[groups.length - 1]
      if (last?.letter === head) last.tunes.push(tune)
      else groups.push({ letter: head, tunes: [tune] })
    }
    return groups
  }, [visible, sort])

  const filtering = query.trim() !== '' || status !== 'all' || rustyOnly || type !== 'all'

  let index = 0
  const delay = () => Math.min(index++, 14) * 35

  return (
    <>
      <Head title="The book" />

      <div className="pb-16">
        <header className="animate-rise mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              The repertoire
            </p>
            <h1 className="font-display text-4xl font-medium tracking-tight text-foreground sm:text-5xl">
              The book
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{tunes.length}</span>{' '}
              {tunes.length === 1 ? 'tune' : 'tunes'}
              {rustyCount > 0 && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => {
                      setRustyOnly(true)
                      setStatus('all')
                    }}
                    className="text-refresh underline-offset-2 hover:underline"
                  >
                    {rustyCount} gone rusty
                  </button>
                </>
              )}
            </p>
          </div>
          <Button asChild>
            <Link href="/tunes/new">Add tune</Link>
          </Button>
        </header>

        <div className="mb-5 space-y-3">
          <div className="relative">
            <span
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            >
              ♪
            </span>
            <Input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tunes by name, type, key, composer…"
              aria-label="Search tunes"
              className="h-11 rounded-lg border-border/80 bg-card/70 pl-9 pr-16 text-base shadow-none"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 font-sans text-[0.65rem] text-muted-foreground sm:block">
              /
            </kbd>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setStatus(f.value)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    status === f.value
                      ? 'border-foreground/80 bg-foreground text-background'
                      : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  )}
                >
                  {f.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setRustyOnly((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  rustyOnly
                    ? 'border-refresh bg-refresh/10 text-refresh'
                    : 'border-border text-muted-foreground hover:border-refresh/50 hover:text-refresh',
                )}
              >
                <span className="size-1.5 rounded-full bg-refresh" />
                Rusty
              </button>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {types.length > 0 && (
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger
                    size="sm"
                    className="w-[8.5rem] bg-card/70"
                    aria-label="Filter by type"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {types.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
                <SelectTrigger size="sm" className="w-[10rem] bg-card/70" aria-label="Sort tunes">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {tunes.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
            No tunes yet. Add your first one.
          </p>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-12 text-center">
            <p className="text-sm text-muted-foreground">No tunes match that.</p>
            <button
              type="button"
              onClick={() => {
                setQuery('')
                setStatus('all')
                setRustyOnly(false)
                setType('all')
              }}
              className="mt-2 text-sm font-medium text-foreground underline-offset-4 hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card/60 shadow-[0_1px_2px_rgba(60,40,20,0.06),0_8px_24px_-14px_rgba(60,40,20,0.16)]">
            {grouped ? (
              grouped.map((group) => (
                <div key={group.letter}>
                  <div className="sticky top-0 z-10 border-y border-border/60 bg-secondary/80 px-5 py-1 font-display text-xs font-semibold tracking-[0.2em] text-muted-foreground backdrop-blur first:border-t-0">
                    {group.letter}
                  </div>
                  <div className="divide-y divide-border/60">
                    {group.tunes.map((tune) => (
                      <Row key={tune.id} tune={tune} delay={delay()} />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="divide-y divide-border/60">
                {visible.map((tune) => (
                  <Row key={tune.id} tune={tune} delay={delay()} />
                ))}
              </div>
            )}
          </div>
        )}

        {filtering && visible.length > 0 && (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Showing {visible.length} of {tunes.length}
          </p>
        )}
      </div>
    </>
  )
}
